/**
 * YodleeFastlink component module.
 * @module investor/pages/YodleeFastlink
 *
 */
import React, {useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import styled from 'styled-components/macro';
import {_openModal} from '../reducers/modals';
import {formatter} from '../utils/utils';

const _YodleeFastlink = styled.div`
  width: 100%;
`;

/**
 * YodleeFastlink component
 * @name YodleeFastlink
 */
const YodleeFastlink = ({
  session: {profile},
  openModal,
  jwt,
  params,
  onSuccess,
  onClose,
  onError,
}) => {
  const scriptIdRef = useRef('yodlee-fastlink-initialize');

  const handleConnectionError = () => {
    const errorMessage = `
      Sorry, we experienced issues connecting to the institution. Please try again later, or you can reach out to \
      ${profile.brand.investorCSEmail} or call ${formatter('phone')(profile.brand.investorCSPhone)}.
    `;

    openModal({
      type: 'Error',
      title: 'Error',
      button: {text: 'OK', action: onError},
      err: {msg: errorMessage},
    });
  };
  const handleFastlinkError = (error) => {
    const isUxError = !error.code;
    const isUnprocessableError = error?.code?.indexOf('N') === 0;
    const errorMessage = `
      Connection process has been interrupted. We were unable to successfully connect to the institution. \
      Tip: Try again and follow the on screen prompts in order to update or confirm the connection \
      details were entered correctly. If you are still experiencing issues reach out to \
      ${profile.brand.investorCSEmail} or call ${formatter('phone')(profile.brand.investorCSPhone)}.
    `;

    if (isUxError || !isUnprocessableError) {
      return;
    }

    openModal({
      type: 'Error',
      title: 'Error',
      button: {text: 'OK', action: onError},
      err: {msg: errorMessage},
    });
  };

  const initializeFastlink = () => {
    try {
      window.fastlink?.close();
      window.fastlink?.open({
        fastLinkURL: profile.yodlee.fastLinkUrl,
        jwtToken: ['Bearer', jwt].join(' '),
        forceIframe: true,
        params: {
          userExperienceFlow: 'Aggregation',
          configName: profile.yodlee.configName,
          ...params,
        },
        onSuccess: (event) => onSuccess({flow: params.flow, ...event}),
        onError: handleFastlinkError,
        onClose: onClose,
      }, 'yodlee-fastlink');
    } catch {
      handleConnectionError();
    }
  };
  const setup = () => {
    if (document.getElementById(scriptIdRef.current)) {
      initializeFastlink();
      return;
    }

    const scriptElement = document.createElement('script');
    scriptElement.type = 'text/javascript';
    scriptElement.id = scriptIdRef.current;
    scriptElement.src = profile.yodlee.fastLinkScript;
    scriptElement.async = true;
    scriptElement.defer = true;
    scriptElement.onload = () => initializeFastlink();
    scriptElement.onerror = () => handleConnectionError();

    document.body.appendChild(scriptElement);
  };
  const cleanup = () => {
    const scriptElement = document.getElementById(scriptIdRef.current);

    window.fastlink?.close();
    document.body.removeChild(scriptElement);
  };

  useEffect(() => {
    setup();

    return () => cleanup();
  }, []);

  return <_YodleeFastlink id='yodlee-fastlink'/>;
};

const mapStateToProps = state => ({
  session: state.session,
});

const mapDispatchToProps = (dispatch) => ({
  openModal: modalToOpen => dispatch(_openModal(modalToOpen)),
});

export default connect(mapStateToProps, mapDispatchToProps)(YodleeFastlink);

YodleeFastlink.defaultProps = {
  params: {},
};

YodleeFastlink.propTypes = {
  /**
   * User Session, includes profile
   */
  session: PropTypes.shape({
    profile: PropTypes.shape({
      brand: PropTypes.shape({
        investorCSEmail: PropTypes.string.isRequired,
        investorCSPhone: PropTypes.string.isRequired,
      }),
      yodlee: PropTypes.shape({
        configName: PropTypes.string.isRequired,
        fastLinkUrl: PropTypes.string.isRequired,
        fastLinkScript: PropTypes.string.isRequired,
      }),
    }),
  }).isRequired,
  /**
   * The open modal function to dispatch _openModal from Redux
   */
  openModal: PropTypes.func.isRequired,
  /**
   * JWT key to access Yodlee API
   */
  jwt: PropTypes.string.isRequired,
  /**
   * Yodlee additional FastLink parameters
   */
  params: PropTypes.shape({
    flow: PropTypes.string,
    providerAccountId: PropTypes.number,
  }),
  /**
   * Yodlee FastLink `onSuccess` callback action
   */
  onSuccess: PropTypes.func.isRequired,
  /**
   * Yodlee FastLink `onClose` callback action
   */
  onClose: PropTypes.func.isRequired,
  /**
   * Yodlee FastLink `onError` callback action (triggered by click on error modal button)
   */
  onError: PropTypes.func.isRequired,
};
